<template>
  <div class="w-full h-full flex flex-col">
    <n-popover
        v-if="solveErrorMessage"
        trigger="manual"
        :show="true"
        placement="left-start"
      >
        <template #trigger>
           <span class="p-0 m-0 w-0 h-0"></span>
        </template>

        <div class="manager-error-message">
          {{ solveErrorMessage }}
        </div>
    </n-popover>

    <div class="h-40px w-full px-12px flex items-center justify-between">
      <n-text strong class="manager-title">{{ headerTitle }}</n-text>

      <div v-if="showActions" class="manager-actions">
          <n-button
            v-if="actionConfig?.confirm"
            size="small"
            secondary
            circle
            :disabled="confirmDisabled"
            title="确定"
            @click="handleConfirm"
          >
            <template #icon>
              <n-icon color="#63E2B7"><Checkmark /></n-icon>
            </template>
          </n-button>
          <n-button
            v-if="actionConfig?.cancel"
            size="small"
            secondary
            circle
            title="取消"
            @click="handleCancel"
          >
            <template #icon>
              <n-icon><Close /></n-icon>
            </template>
          </n-button>
          <n-button
            v-if="actionConfig?.preview"
            size="small"
            tertiary
            circle
            title="预览"
            @click="handlePreviewToggle"
          >
            <template #icon>
              <n-icon><View /></n-icon>
            </template>
          </n-button>
          <n-button
            v-if="actionConfig?.help"
            size="small"
            tertiary
            circle
            title="帮助"
            @click="handleHelp"
          >
            <template #icon>
              <n-icon><Help /></n-icon>
            </template>
          </n-button>
      </div>
    </div>

    <n-divider class="!m-0" />

    <div class="manager-content">
      <div v-if="readonlyPreview" class="manager-empty">
        <n-empty
          :description="READONLY_PREVIEW_MESSAGE"
          size="small"
        />
      </div>
      <template v-else-if="visibleSections.length">
        <div
          v-for="section in visibleSections"
          :key="section.id"
          class="manager-section"
        >
          <!-- <div class="section-title">
            <n-text strong>{{ section.title }}</n-text>
          </div> -->
          <div class="section-body">
            <component :is="section.component" :context="context" />
          </div>
        </div>
      </template>
      <div v-else-if="!hideEmptyMessage" class="manager-empty">
        <n-empty
          description="选择一实体来查看或修改其属性"
          size="small"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { NButton, NEmpty, NIcon, NPopover, NText } from 'naive-ui';
import { Checkmark, Close, Help, View } from '@vicons/carbon';
import {
  getPropertyManagerSections,
  resolvePropertyManagerSolveErrorMessage,
  type PropertyManagerActions,
  type PropertyManagerContext,
} from '@/core';

interface PropertyManagerProps {
  context: PropertyManagerContext;
  forceConfirm?: boolean;
}

const props = defineProps<PropertyManagerProps>();

const READONLY_PREVIEW_MESSAGE = '历史版本仅支持查看';

const externalForceConfirm = computed(() => Boolean(props.forceConfirm));
const visibleSections = computed(() => {
  const sections = getPropertyManagerSections();
  const applicableSections = sections.filter((section) => {
    if (!section.isApplicable) {
      return true;
    }
    return section.isApplicable(props.context);
  });

  /**
   * 独占命令会话统一由 section 注册声明：
   * 1. 裁剪、转换实体、装配配合等命令激活后只允许展示自身面板；
   * 2. 宿主只识别通用扩展位，不硬编码业务 section ID；
   * 3. 新命令按注册协议扩展即可，避免主属性栏持续耦合业务模块。
   */
  const exclusiveSection = applicableSections.find((section) => (
    section.exclusive === true
    || typeof section.exclusiveGroup === 'string'
  ));
  if (exclusiveSection) {
    return [exclusiveSection];
  }

  return applicableSections;
});

const activeSection = computed(() => visibleSections.value[0] ?? null);
const sectionConfirmSignal = ref(0);
let unsubscribeActiveSectionConfirmState: (() => void) | null = null;

/**
 * 订阅当前 section 暴露的确认状态变化。
 *
 * 设计说明：
 * PropertyManager 宿主只关心“确认按钮状态需要重算”，不读取具体业务 controller，也不把渲染核心对象放入响应式状态。
 * 各业务 section 负责把自身控制器状态映射为普通布尔值，从而保持属性栏与业务逻辑解耦。
 */
function subscribeActiveSectionConfirmState(): void {
  unsubscribeActiveSectionConfirmState?.();
  unsubscribeActiveSectionConfirmState = null;

  const unsubscribe = activeSection.value?.subscribeConfirmState?.(props.context, () => {
    sectionConfirmSignal.value += 1;
  });
  unsubscribeActiveSectionConfirmState = typeof unsubscribe === 'function' ? unsubscribe : null;
  sectionConfirmSignal.value += 1;
}

watch(
  () => [
    activeSection.value?.id ?? null,
    props.context.renderer,
  ] as const,
  () => {
    subscribeActiveSectionConfirmState();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  unsubscribeActiveSectionConfirmState?.();
  unsubscribeActiveSectionConfirmState = null;
});

const getSectionTitle = (section: typeof activeSection.value): string => {
  if (!section) {
    return '属性';
  }
  const title = section.title;
  if (typeof title === 'function') {
    return title(props.context);
  }
  return title;
};

const headerTitle = computed(() => getSectionTitle(activeSection.value));
const solveErrorMessage = computed(() => {
  /**
   * Mate PropertyManager 的顶部诊断由外层宿主展示，但真实状态来自 cad-core 控制器订阅。
   * 这里复用 section 的状态信号，避免按钮已切换到新配合类型而顶部仍缓存旧求解错误。
   */
  void sectionConfirmSignal.value;
  return resolvePropertyManagerSolveErrorMessage(props.context);
});
const actionConfig = computed<PropertyManagerActions | null>(() => {
  const resolvedActions = activeSection.value?.resolveActions?.(props.context);
  if (resolvedActions) {
    return resolvedActions;
  }
  const config = activeSection.value?.actions ?? null;
  if (config) {
    return config;
  }
  if (externalForceConfirm.value) {
    return { confirm: true };
  }
  return null;
});
const showActions = computed(() => {
  if (readonlyPreview.value) {
    return false;
  }
  if (hideEmptyMessage.value) {
    return false;
  }
  const config = actionConfig.value;
  if (config && (config.confirm || config.cancel || config.preview || config.help)) {
    return true;
  }
  return externalForceConfirm.value;
});

const readonlyPreview = computed(() => props.context.readonlyPreview === true);

const confirmDisabled = computed(() => {
  if (readonlyPreview.value) {
    return true;
  }
  /**
   * 读取信号值用于建立响应式依赖。具体业务状态由 section 自己订阅并触发重算，
   * 宿主不直接持有业务控制器，避免把 PropertyManager 固化到某一个命令实现上。
   */
  void sectionConfirmSignal.value;
  const activeSectionConfirmDisabled = activeSection.value?.resolveConfirmDisabled?.(props.context);
  if (typeof activeSectionConfirmDisabled === 'boolean') {
    return activeSectionConfirmDisabled;
  }
  return false;
});

const hideEmptyMessage = computed(() => {
  return props.context.hideEmptyMessage === true;
});

function handleConfirm(): void {
  /**
   * 历史版本预览需要同时做 UI 隐藏与提交入口硬拦截：
   * 前者减少误操作，后者防止旧事件或测试脚本绕过按钮状态直接触发确认链路。
   */
  if (readonlyPreview.value) {
    return;
  }
  if (confirmDisabled.value) {
    return;
  }
  if (activeSection.value?.onConfirm) {
    activeSection.value.onConfirm(props.context);
    return;
  }
  props.context.onConfirm?.();
}

function handleCancel(): void {
  if (activeSection.value?.onCancel) {
    activeSection.value.onCancel(props.context);
    return;
  }
  props.context.onCancel?.();
}

function handleHelp(): void {
  if (activeSection.value?.onHelp) {
    activeSection.value.onHelp(props.context);
    return;
  }
  props.context.onHelp?.();
}

function handlePreviewToggle(): void {
  if (activeSection.value?.onPreviewToggle) {
    activeSection.value.onPreviewToggle(props.context);
    return;
  }
  props.context.onPreviewToggle?.();
}
</script>

<style scoped>
.manager-title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.manager-error-message {
  max-width: 260px;
  font-size: 12px;
  line-height: 1.5;
  white-space: normal;
}

.manager-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 12px;
}

.manager-content {
  flex: 1;
  overflow: auto;
  padding: 12px 12px 24px;
}

.manager-section + .manager-section {
  margin-top: 16px;
}

.section-title {
  margin-bottom: 8px;
}

.section-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.manager-empty {
  margin-top: 40px;
}
</style>
